import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';

interface PreviewInfo {
  version?: string;
  sha?: string;
  author?: string;
  time?: string;
  branch?: string;
  prNumber?: string;
  prUrl?: string;
}

// A single JSON blob inlined at build time (next build) from
// NEXT_PUBLIC_PREVIEW_INFO, which CI sets ONLY on preview (pull_request) builds.
// On production (main) builds it is empty, so the badge never renders.
// See the Dockerfile + ci.yml.
function parsePreviewInfo(): PreviewInfo | null {
  const raw = process.env.NEXT_PUBLIC_PREVIEW_INFO;
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PreviewInfo;
  } catch {
    return null;
  }
}

const info = parsePreviewInfo();

/**
 * Floating "Preview" badge, shown only when running a preview (sha) build in
 * dev. Collapsed it is a small Mapped-yellow tab on the right edge; clicking it
 * reveals the build details and a link to the PR.
 */
export const PreviewBadge: FunctionComponent = () => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Render nothing on production builds (no preview metadata) and during SSR
  // (avoids a hydration mismatch from the relative timestamp).
  if (!info?.version || !mounted) return null;

  const relative = info.time ? DateTime.fromISO(info.time).toRelative() : null;

  return (
    <Wrap ref={ref}>
      {open && (
        <Card role="dialog" aria-label="Preview build details">
          <Title>Preview build</Title>
          <Dl>
            <dt>Version</dt>
            <dd>{info.version}</dd>
            {info.branch && (
              <>
                <dt>Branch</dt>
                <dd>{info.branch}</dd>
              </>
            )}
            {info.author && (
              <>
                <dt>By</dt>
                <dd>{info.author}</dd>
              </>
            )}
            {relative && (
              <>
                <dt>Built</dt>
                <dd>{relative}</dd>
              </>
            )}
            {info.sha && (
              <>
                <dt>Commit</dt>
                <dd>{info.sha.slice(0, 8)}</dd>
              </>
            )}
          </Dl>
          {info.prUrl && (
            <PrButton href={info.prUrl} target="_blank" rel="noreferrer">
              View PR{info.prNumber ? ` #${info.prNumber}` : ''} ↗
            </PrButton>
          )}
        </Card>
      )}

      <Tab
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Preview build info"
      >
        <Dot />
        Preview
      </Tab>
    </Wrap>
  );
};

// Mapped yellow (theme.colors.yellow)
const YELLOW = '#F2C94C';

const Wrap = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  z-index: 2147483000;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: inherit;
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  background: ${YELLOW};
  color: #1a1a1a;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 10px 12px;
  border-radius: 10px 0 0 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  writing-mode: vertical-rl;
  text-orientation: mixed;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #1a1a1a;
`;

const Card = styled.div`
  background: #ffffff;
  color: #1a1a1a;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-top: 3px solid ${YELLOW};
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  padding: 16px 18px;
  min-width: 220px;
  max-width: 280px;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
  color: #6b6b6b;
`;

const Dl = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 14px;
  margin: 0 0 14px;

  dt {
    color: #8a8a8a;
    font-size: 12px;
  }
  dd {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    word-break: break-word;
    text-align: right;
  }
`;

const PrButton = styled.a`
  display: block;
  text-align: center;
  background: #1a1a1a;
  color: #ffffff;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 12px;
  border-radius: 8px;

  &:hover {
    opacity: 0.9;
  }
`;
